import { v4 as uuid } from 'uuid';

import {
  getAllProvidedSlots,
  getArticleDependentOnRoot,
  getColor,
  getFittingArticle,
  getPossibleSingleSlotsToMoveArticle,
  getPreferredSlot,
  getRequiredConnectArticles,
} from '../api/core-service/configurator/coreService';
import { getMultipleArticleDefinitions } from '../api/core-service/configurator/getMultipleArticleDefinitions';
import { getMultipleColors } from '../api/core-service/configurator/getMultipleColors';
import { getArticleForParameterValuesApiCall } from '../api/core-service/wsc/articles/getArticleForParameterValuesApiCall';
import { ColorValueDto } from '../api/core-service/wsc/colors/useColorApi';
import {
  HEIGHT_CONTROL_PANEL_INITIAL_POSITION,
  HEIGHT_CONTROL_PANEL_UUID,
  HEIGHT_CONTROL_PANEL_WIDTH,
} from '../constants';
import { Article, ArticleDefinition, BaseArticle, RequiredArticle } from '../interfaces/Article';
import { ArticleColor } from '../interfaces/ArticleColor';
import { BlockedSlotMapper } from '../interfaces/BlockedSlot';
import { AffectedMaterial, Color } from '../interfaces/ColorValue';
import { Configuration } from '../interfaces/Configuration';
import ConnectableSlots, {
  ConnectableSlotsAndRequiresConnectArticle,
} from '../interfaces/ConnectableSlots';
import { SingleSlot, Slot } from '../interfaces/Slot';
import { SlotDefinition, SlotType } from '../interfaces/SlotDefinition';
import { gridlineTheme } from '../interfaces/Theme';
import { ArticleDefinitionMapper } from '../interfaces/mappers/ArticleDefinitionMapper';
import { RequiredConnectArticleMapper } from '../interfaces/mappers/RequiredConnectArticleMapper';
import { SingleSlotWithParentArticleNumberMapper } from '../interfaces/mappers/SingleSlotWithParentArticleNumberMapper';
import { SlotDefinitionMapper } from '../interfaces/mappers/SlotDefinitionMapper';
import SlotService from './SlotService';

const instantiateHeightControlPanel = {
  id: HEIGHT_CONTROL_PANEL_UUID,
  position: HEIGHT_CONTROL_PANEL_INITIAL_POSITION,
  size: HEIGHT_CONTROL_PANEL_WIDTH,
};

export class ArticleService {
  primaryColor?: ColorValueDto;
  secondaryColor?: ColorValueDto;
  defaultTheme = { ...gridlineTheme };

  instantiateArticle = async (definition: ArticleDefinition): Promise<[Article, Slot[]]> => {
    const [articleColorParams, slotDefinitions] = await Promise.all([
      this.getArticleColors(definition.number),
      this.getSlotsForArticle(definition.number),
    ]);

    const article = {
      definition: definition,
      id: uuid(),
      colors: articleColorParams,
      requiredConnectArticleId: undefined,
      requiredLightingArticlesIds: [],
      number: definition.number,
    };

    const slots = slotDefinitions.map((definition) => this.instantiateSlot(definition, article));

    return [article, slots];
  };

  private instantiateSlot = (definition: SlotDefinition, article: Article): Slot => {
    if (definition.type === SlotType.LINE) {
      const children = article.definition.hasHeightControlPanel
        ? [instantiateHeightControlPanel]
        : [];

      return {
        type: SlotType.LINE,
        definition,
        id: uuid(),
        parentId: article.id,
        children,
        blockedBy: undefined,
      };
    }

    if (definition.type === SlotType.SINGLE) {
      return {
        type: SlotType.SINGLE,
        definition,
        id: uuid(),
        parentId: article.id,
        blockedBy: undefined,
        childId: undefined,
      };
    }

    return {
      type: SlotType.PAIR,
      definition,
      id: uuid(),
      parentId: article.id,
      blockedBy: undefined,
      childId: undefined,
    };
  };

  instantiateRequiredArticle = (
    articleDefinition: ArticleDefinition,
    amount = 1,
  ): RequiredArticle => ({
    definition: articleDefinition,
    id: uuid(),
    colors: [],
    number: articleDefinition.number,
    amount,
  });

  loadDefinitions = async <T extends BaseArticle>(articles: T[]): Promise<T[]> => {
    if (articles.length === 0) {
      return [];
    }

    const articleNumbers = articles.map((a) => a.number);

    const allArticleDefinitions = (await getMultipleArticleDefinitions(articleNumbers)).map(
      (definitionDto) => ({
        articleNumber: definitionDto.articleNumber,
        definition: ArticleDefinitionMapper.toDomain(definitionDto.definition),
      }),
    );

    const colorMappings = await getMultipleColors(articleNumbers);

    return articles.map((article) => ({
      ...article,
      definition: allArticleDefinitions.find((d) => d.articleNumber === article.number).definition,
      colors: article.colors.map((color) => ({
        ...color,
        definition: {
          materialName: colorMappings
            .find((articleColor) => articleColor.articleNumber === article.number)
            .colors.find((articleColor) => articleColor.affectedMaterial === color.affectedMaterial)
            .localisedName,
        },
      })),
    }));
  };

  getArticleColors = async (articleNumber: string): Promise<ArticleColor[]> => {
    const response = await getColor(articleNumber);

    return response.map((color: Color) => {
      const colorRalValue =
        color.affectedMaterial === AffectedMaterial.CONFIG_RAL_PRIMARY
          ? this.primaryColor?.ralValue || this.defaultTheme.config_ral_primary
          : this.secondaryColor?.ralValue || this.defaultTheme.config_ral_secondary;

      return {
        affectedMaterial: color.affectedMaterial,
        definition: {
          materialName: color.localisedName,
        },
        ralValue: colorRalValue,
      };
    });
  };

  getPreferredSlot = async (
    articleDefinition: ArticleDefinition,
    configuration: Configuration,
    parent?: string,
  ): Promise<ConnectableSlotsAndRequiresConnectArticle | Error> => {
    const allSlots = parent
      ? configuration.slots.filter((slot) => slot.parentId === parent)
      : configuration.slots;

    const availableSlots = allSlots
      .filter((slot) =>
        SlotService.isSlotAvailableForArticle(slot, configuration.articles, articleDefinition),
      )
      .map((slot) =>
        SingleSlotWithParentArticleNumberMapper.from(
          slot,
          configuration.getArticle(slot.parentId)?.number,
        ),
      );

    const usedSlots = allSlots
      .filter(SlotService.isSlotOccupied)
      .filter((slot) => slot.type === SlotType.SINGLE)
      .map((slot: SingleSlot) =>
        BlockedSlotMapper.from(slot, configuration.getArticle(slot.childId)?.number),
      );

    return await getPreferredSlot(articleDefinition.number, availableSlots, usedSlots);
  };

  getRequiredConnectArticle = async (
    articleNumber: string,
    slot: Slot,
  ): Promise<RequiredArticle | Error> => {
    const slotDefinitionDto = SlotDefinitionMapper.toDto(slot.definition);
    const response = await getRequiredConnectArticles(articleNumber, slotDefinitionDto);

    if (!response || response.status !== 200) {
      return new Error(
        `getRequiredConnectArticles request for article number ${articleNumber} is not successful`,
      );
    }

    const requiredArticle = RequiredConnectArticleMapper.toDomain(response.data);

    const colors = await this.getArticleColors(requiredArticle.number);
    if (colors instanceof Error) {
      return colors;
    }
    requiredArticle.colors = colors;

    return requiredArticle;
  };

  getPossibleArticlePositions = async (
    configuration: Configuration,
    articleId: string,
    parentSlots: Slot[],
    parentArticleNumber: string,
  ): Promise<ConnectableSlots[]> => {
    const article = configuration.getArticle(articleId);

    const availableSlots = parentSlots
      .filter((slot) =>
        SlotService.isSlotAvailableForArticle(slot, configuration.articles, article.definition),
      )
      .map((slot) => SingleSlotWithParentArticleNumberMapper.from(slot, parentArticleNumber));

    const usedSlots = parentSlots
      .filter(SlotService.isSlotOccupied)
      .filter((slot) => slot.type === SlotType.SINGLE)
      .map((slot: SingleSlot) =>
        BlockedSlotMapper.from(slot, configuration.getArticle(slot.childId)?.number),
      );

    return await getPossibleSingleSlotsToMoveArticle(article.number, availableSlots, usedSlots);
  };

  getFittingArticle = async (
    productNumber: string,
    rootArticleNumber: string,
  ): Promise<ArticleDefinition | Error> => {
    const request: CoreServiceAPI.GetFittingArticleForRootBody = {
      rootArticleNumber: rootArticleNumber,
    };

    const response = await getFittingArticle(productNumber, request);

    if (!response || response.status !== 200) {
      return new Error(
        `getFittingArticle request for product group ${productNumber} is not successful`,
      );
    }

    return ArticleDefinitionMapper.toDomain(response.data);
  };

  getArticleDependentOnRoot = async (
    previousArticleNumber: string,
    rootArticleNumber: string,
  ): Promise<[Article, Slot[]]> => {
    const newArticleDefinition = await getArticleDependentOnRoot(
      previousArticleNumber,
      rootArticleNumber,
    );

    return this.instantiateArticle(newArticleDefinition);
  };

  getNewArticleAfterParameterValuesChange = async (
    parameterValueIds: string[],
    changedParameterValueId: string,
    rootArticleNumber: string,
  ): Promise<[Article, Slot[]]> => {
    try {
      const definition = (await getArticleForParameterValuesApiCall(
        rootArticleNumber,
        parameterValueIds,
        changedParameterValueId,
      )) as ArticleDefinition;
      const articleDefinition = ArticleDefinitionMapper.toDomain(
        definition as CoreServiceAPI.ArticleDto,
      );
      return await this.instantiateArticle(articleDefinition);
    } catch {
      throw new Error(
        `Could not determine article for parameter values ${parameterValueIds} and root article number ${rootArticleNumber}.`,
      );
    }
  };

  getNewRequiredArticleAfterParameterValuesChange = async (
    parameterValueIds: string[],
    changedParameterValueId: string,
    rootArticleNumber: string,
    amount: number,
  ): Promise<RequiredArticle> => {
    try {
      const definition = (await getArticleForParameterValuesApiCall(
        rootArticleNumber,
        parameterValueIds,
        changedParameterValueId,
      )) as ArticleDefinition;
      const articleDefinition = ArticleDefinitionMapper.toDomain(
        definition as CoreServiceAPI.ArticleDto,
      );
      return await this.instantiateRequiredArticle(articleDefinition, amount);
    } catch {
      throw new Error(
        `Could not determine article for parameter values ${parameterValueIds} and root article number ${rootArticleNumber}.`,
      );
    }
  };

  getSlotsForArticle = async (articleNumber: string): Promise<SlotDefinition[]> => {
    const response = await getAllProvidedSlots(articleNumber);

    if (!response || response.status !== 200) {
      throw new Error(`Failed to get provided slots for article ${articleNumber}`);
    }

    return response.data.map((slotDto: CoreServiceAPI.SlotDefinitionDto) =>
      SlotDefinitionMapper.toDomain(slotDto),
    );
  };
}

export const articleService: ArticleService = new ArticleService();
